import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '@/api';
import { Alert, Button } from '@/components/ui';
import { useToast } from '@/components/ui';
import { CodeBoxes } from '@/features/auth/CodeBoxes';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { dashboardFor } from '@/routes/paths';
import { otpSchema } from '@/validations';

import { AuthCard } from './AuthCard';

const RESEND_SECONDS = 60;

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, refreshUser, pendingVerificationCode } = useAuth();

  const email = searchParams.get('email') ?? user?.email ?? '';
  // Arriving straight from sign-up or sign-in, a code is already on its way,
  // and the API refuses another for a minute.
  const [cooldown, setCooldown] = useState(location.state?.codeSent ? RESEND_SECONDS : 0);
  // Registration hands the code over in local development, where the console
  // email provider is used and no message is actually delivered.
  const [debugCode, setDebugCode] = useState(location.state?.debugCode ?? pendingVerificationCode);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(otpSchema) });

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const resend = useMutation({
    mutationFn: () => authApi.sendVerificationCode(email),
    onSuccess: (result) => {
      setCooldown(result.resend_available_in_seconds || RESEND_SECONDS);
      setDebugCode(result.debug_code ?? null);
      toast.success('A new code is on its way.');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not send the code')),
  });

  async function onSubmit(values) {
    try {
      await authApi.verifyEmail(email, values.code);
      // The session user carries the verified flag, so refresh it.
      if (user) await refreshUser();
      toast.success('Email verified.');
      navigate(dashboardFor(user?.role ?? 'STUDENT'), {
        replace: true,
      });
    } catch (error) {
      setError('code', { message: errorMessage(error, 'That code did not work') });
    }
  }

  return (
    <AuthCard
      title="Verify your email"
      description={
        email ? `Enter the 6-digit code we sent to ${email}.` : 'Enter your verification code.'
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {debugCode && (
          <Alert tone="info" title="Development mode">
            No mail provider is configured, so the code is shown here:{' '}
            <span className="font-mono font-semibold">{debugCode}</span>
          </Alert>
        )}

        <div className="space-y-1.5">
          <Controller
            name="code"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <CodeBoxes
                value={field.value}
                onChange={field.onChange}
                // The sixth digit sends it; the button is there for a retry.
                onComplete={() => void handleSubmit(onSubmit)()}
                disabled={isSubmitting}
                invalid={Boolean(errors.code)}
                autoFocus
                label="Verification code"
              />
            )}
          />
          {errors.code && (
            <p className="text-caption text-danger-600" role="alert">
              {errors.code.message}
            </p>
          )}
        </div>

        <Button type="submit" fullWidth isLoading={isSubmitting}>
          Verify email
        </Button>

        <Button
          type="button"
          variant="ghost"
          fullWidth
          disabled={cooldown > 0 || resend.isPending || !email}
          onClick={() => resend.mutate()}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </Button>
      </form>
    </AuthCard>
  );
}
